import * as React from 'react'
import loadable from '@loadable/component'
import dynamic from 'next/dynamic'

import { i18n, useSiteConfig } from '@thg-commerce/enterprise-core'
import {
  styled,
  useTheme,
} from '@thg-commerce/enterprise-pages/src/Product/theme'
import { Button, Separator } from '@thg-commerce/gravity-elements'
import { spacing, Text } from '@thg-commerce/gravity-theme'

import { EnhancedBuyNowPayLaterModalPresenterProps } from './EnhancedBuyNowPayLaterModalPresenter'
import { Title } from './styles'

const EnhancedBuyNowPayLaterModalPresenter = dynamic<
  EnhancedBuyNowPayLaterModalPresenterProps
>(() =>
  import('./EnhancedBuyNowPayLaterModalPresenter').then(
    (mod) => mod.EnhancedBuyNowPayLaterModalPresenter,
  ),
)

const EnhancedBNPLWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`

const EnhancedBNPLContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
`

const StyledLink = styled(Button)`
  ${Text('bodyText', 'alternate')}
`

const StyledBuyNowTitle = styled.p`
  ${Text('bodyText', 'default')};
`

const StyledBuyNowSubTitle = styled.p`
  margin: 0;
  ${Text('small', 'default')};
  color: #747474;
`

const OptionsContainer = styled.div<{
  alternativeStyle?: boolean
}>`
  margin-top: ${spacing(2)};
  margin-bottom: ${spacing(2)};
  padding: ${spacing(2)};
  border-radius: ${(props) =>
    props.theme.pageTheme.buyNowPayLater?.borderRadius ?? `8px`};
  background-color: ${(props) =>
    props.alternativeStyle
      ? props.theme.pageTheme.buyNowPayLater?.alternativeStyle?.backgroundColor
      : props.theme.pageTheme.buyNowPayLater?.backgroundColor ||
        props.theme.colors.palette.brand.lightest};

  border: ${(props) =>
    props.alternativeStyle &&
    props.theme.pageTheme.buyNowPayLater?.alternativeStyle?.border};

  li {
    align-items: center;
    margin-top: 0;
    &:not(:last-child) {
      margin-bottom: ${spacing(1)};
    }
  }
  li > p {
    margin-top: 0;
  }
`

const ConditionalSeparator = ({
  hideSeparator,
}: {
  hideSeparator: boolean | undefined
}) => {
  if (hideSeparator) {
    return null
  }
  return (
    <Separator
      withSpacing
      separatorMargin={{ top: 2, right: 0, bottom: 2, left: 0 }}
    />
  )
}

const ClearPayPillIcon = loadable(
  () =>
    import(
      '@thg-commerce/gravity-icons/src/components/PaymentIcons/ClearPayPill'
    ),
)

const KlarnaIcon = loadable(
  () =>
    import(
      '@thg-commerce/gravity-icons/src/components/PaymentIcons/KlarnaPill'
    ),
)

const Klarna2025Icon = loadable(
  () =>
    import(
      '@thg-commerce/gravity-icons/src/components/PaymentIcons/Klarna2025'
    ),
)

const getIconMap = (
  useKlarna2025Branding?: boolean,
): Record<string, React.ComponentType> => ({
  clearpay: ClearPayPillIcon,
  klarna: useKlarna2025Branding ? Klarna2025Icon : KlarnaIcon,
})

const EnhancedBNPLOptionsContainer = ({
  i18nTextValues,
  titleVisible,
  enableEnhancedBNPLModal,
  onInfoClick,
  showEnhancedPaymentProviders,
  iconMap,
}: {
  i18nTextValues: {
    bnplTitleText: string
    enhancedTitleText: string
    enhancedSubtitleText: string
    learnMoreText: string
  }
  titleVisible: boolean
  enableEnhancedBNPLModal?: boolean
  onInfoClick: (event: React.MouseEvent) => void
  showEnhancedPaymentProviders?: string[]
  iconMap: Record<string, React.ComponentType>
}) => (
  <OptionsContainer alternativeStyle={enableEnhancedBNPLModal}>
    {titleVisible && <Title>{i18nTextValues.bnplTitleText}</Title>}
    <div>
      <EnhancedBNPLWrapperDiv>
        {(showEnhancedPaymentProviders || []).map((provider) => {
          const IconComponent = iconMap[provider.toLowerCase()]
          return IconComponent ? <IconComponent key={provider} /> : null
        })}
      </EnhancedBNPLWrapperDiv>
      <StyledBuyNowTitle>{i18nTextValues.enhancedTitleText}</StyledBuyNowTitle>
      <EnhancedBNPLContainer>
        <StyledBuyNowSubTitle>
          {i18nTextValues.enhancedSubtitleText}
        </StyledBuyNowSubTitle>
        <StyledLink onClick={onInfoClick} renderedAs="a" emphasis="low" href="">
          {i18nTextValues.learnMoreText}
        </StyledLink>
      </EnhancedBNPLContainer>
    </div>
  </OptionsContainer>
)

export const EnhancedBNPLWrapper = () => {
  const theme = useTheme()

  const {
    enableEnhancedBNPLModal,
    showPaymentProviders,
    useKlarna2025Branding,
  } = useSiteConfig()

  const iconMap = getIconMap(useKlarna2025Branding)

  const [isEnhancedBNPLModalOpen, setEnhancedBNPLModalOpen] = React.useState(
    false,
  )

  const handleInfoButtonClick = (event: React.MouseEvent) => {
    event.preventDefault()
    setEnhancedBNPLModalOpen(true)
  }

  const i18nTextValues = {
    bnplTitleText: i18n('product.buynowpaylater.title.text'),
    bnplGenericText: i18n('product.buynowpaylater.generic.text'),
    bnplViewoptionsText: i18n('product.buynowpaylater.viewoptions.text'),
    bnplCloseText: i18n('general.modal.close.button.arialabel'),
    enhancedTitleText: i18n('product.enhanced.buynowpaylater.title.text'),
    enhancedSubtitleText: i18n('product.enhanced.buynowpaylater.subtitle.text'),
    learnMoreText: i18n('product.buynowpaylaterproviders.learnmore'),
  }

  return (
    <React.Fragment>
      <EnhancedBNPLOptionsContainer
        i18nTextValues={i18nTextValues}
        titleVisible={theme.pageTheme.buyNowPayLater?.title?.show ?? true}
        enableEnhancedBNPLModal={enableEnhancedBNPLModal}
        onInfoClick={handleInfoButtonClick}
        showEnhancedPaymentProviders={showPaymentProviders}
        iconMap={iconMap}
      />
      <EnhancedBuyNowPayLaterModalPresenter
        isOpen={isEnhancedBNPLModalOpen}
        setIsOpen={setEnhancedBNPLModalOpen}
        slideUpOnMobile={enableEnhancedBNPLModal}
        headerHeight={47}
      />
      <ConditionalSeparator
        hideSeparator={theme.pageTheme.buyNowPayLater?.hideSeparator}
      />
    </React.Fragment>
  )
}
